import { useEffect } from "react";
import { useConfigContext } from "@/ConfigProvider";
import { useAutocomplete } from "@/autocomplete/context/GlobalContext";
import { useInputStyling } from "@/autocomplete/hooks/useInputStyling";
import { useAnimatedPlaceholder } from "@/autocomplete/hooks/useAnimatedPlaceholder";
import { SearchInput } from "@/autocomplete/components/SearchInput";
import { MIN_QUERY_LENGTH } from "@/utils/searchConfig";

export function Form() {
    const {
        inputRef,
        formRef,
        autocomplete,
        isModalOpen,
        setIsModalOpen,
        forceModalOpen,
        setForceModalOpen,
        autocompleteState: { query, status },
    } = useAutocomplete();
    const { tc } = useConfigContext();

    useInputStyling(inputRef, query);

    const placeholders = [
        tc.search_placeholder_text_1,
        tc.search_placeholder_text_2,
        tc.search_placeholder_text_3,
        tc.search_placeholder_text_4,
        tc.search_placeholder_text_5,
        tc.search_placeholder_text_6,
    ];
    useAnimatedPlaceholder(inputRef, placeholders);

    useEffect(() => {
        if (isModalOpen && inputRef?.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    }, [isModalOpen, inputRef]);

    const originalFormProps = autocomplete.getFormProps({ inputElement: inputRef.current });
    const originalInputProps = autocomplete.getInputProps({ inputElement: inputRef.current });

    const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        if (!query?.trim()) {
            e.preventDefault();
            return;
        }
        originalFormProps.onSubmit?.(e);
    };

    useEffect(() => {
        // Keep the dropdown closed below the minimum query length, and while a
        // search is still in flight (debounce/loading/stalled), so the panel
        // only ever appears once there's something to show: results or the
        // "no results" message.
        const hasQuery = (query?.trim().length ?? 0) >= MIN_QUERY_LENGTH;
        const isSettled = status !== "loading" && status !== "stalled";
        if (!forceModalOpen) {
            setIsModalOpen(hasQuery && isSettled);
            autocomplete.setIsOpen(hasQuery && isSettled);
        } else {
            autocomplete.setIsOpen(true);
        }
    }, [query, status, setIsModalOpen, autocomplete, forceModalOpen, setForceModalOpen]);

    const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        originalInputProps.onKeyDown?.(e);
        if (e.key === "Escape") {
            setIsModalOpen(false);
            setForceModalOpen(false);
            autocomplete.setIsOpen(false);
        }
    };

    return (
        <form
            ref={formRef}
            className="algoliaAutocompleteForm is-loaded"
            {...originalFormProps}
            onSubmit={onSubmit}
        >
                <div className="inputGroup searchBar algoliaAutocompleteForm__searchBar">
                    <label className="algoliaAutocompleteForm__label" {...autocomplete.getLabelProps({})}>
                        <button className="inputGroup__addon" type="submit" aria-label={tc.search} title={tc.search}>
                            <i className="icon icon-search" aria-hidden="true"></i>
                        </button>
                    </label>
                    <SearchInput
                        ref={inputRef}
                        {...originalInputProps}
                        onKeyDown={onInputKeyDown}
                        query={query}
                    />
                    {query && (
                        <button className="algoliaAutocompleteForm__clear" title="Clear" type="reset">
                            <i className="icon icon-close" aria-hidden={true}></i>
                        </button>
                    )}
                </div>
        </form>
    );
}
